import { useEffect, useState } from "react";
import { Eye, MousePointerClick, Phone, Mail, FileText, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { AdminShell } from "./AdminShell";

const PAGE_LABEL = {
  home: "Home", about: "About", projects: "Recent Projects", project_detail: "Project detail",
  investment: "Property Investment", sell: "Sell Your Property", estate_agents: "For Estate Agents",
  contact: "Contact", privacy: "Privacy", opportunities: "Opportunities", opportunity_detail: "Opportunity detail",
};

function BarList({ title, items, empty }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5">
      <h3 className="text-sm font-semibold text-white mb-4">{title}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-emerald-100/50">{empty}</p>
      ) : (
        <div className="space-y-2.5">
          {items.map((i) => (
            <div key={i.label} className="flex items-center gap-3 text-sm">
              <div className="w-32 shrink-0 text-emerald-100/80 truncate capitalize">{PAGE_LABEL[i.label] || i.label}</div>
              <div className="flex-1 bg-white/5 rounded-full h-2.5 overflow-hidden">
                <div className="h-full bg-brass rounded-full" style={{ width: `${(i.count / max) * 100}%` }} />
              </div>
              <div className="w-10 text-right font-mono text-white">{i.count}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/summary").then(({ data }) => setData(data)).catch(() => {}); }, []);

  if (!data) return <AdminShell title="Analytics"><p className="text-emerald-100/50">Loading…</p></AdminShell>;

  const f = data.funnel || {};
  const cards = [
    { label: "Page views", value: data.total_page_views, icon: Eye, tone: "text-blue-300" },
    { label: "Seller form starts", value: f.seller_form_start, icon: FileText, tone: "text-emerald-300" },
    { label: "Seller completions", value: f.seller_form_complete, icon: FileText, tone: "text-teal-300" },
    { label: "Seller conversion", value: `${data.seller_form_conversion}%`, icon: TrendingUp, tone: "text-brass" },
    { label: "CTA clicks", value: f.cta_click, icon: MousePointerClick, tone: "text-purple-300" },
    { label: "Phone clicks", value: f.phone_click, icon: Phone, tone: "text-amber-300" },
    { label: "Email clicks", value: f.email_click, icon: Mail, tone: "text-indigo-300" },
    { label: "Opportunity interest", value: f.opportunity_interest, icon: TrendingUp, tone: "text-cyan-300" },
  ];
  const maxDay = Math.max(1, ...data.by_day.map((d) => d.count));

  return (
    <AdminShell title="Analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {cards.map((c) => (
          <div key={c.label} data-testid={`analytics-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-[#0F2E23] rounded-xl border border-white/10 p-4">
            <div className="flex items-center justify-between"><span className="text-xs text-emerald-100/60">{c.label}</span><c.icon className={`w-4 h-4 ${c.tone}`} /></div>
            <div className="mt-2 text-2xl font-mono font-semibold text-white">{c.value ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5 mb-6" data-testid="analytics-traffic-chart">
        <h3 className="text-sm font-semibold text-white mb-4">Page views — last 14 days</h3>
        <div className="flex items-end gap-1.5 h-40">
          {data.by_day.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center justify-end group">
              <div className="w-full bg-brass/80 hover:bg-brass rounded-t transition-all" style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count ? "4px" : "0" }} title={`${d.date}: ${d.count}`} />
              <span className="mt-1.5 text-[9px] text-emerald-100/40">{d.date.slice(5)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BarList title="Top pages" items={data.top_pages} empty="No page views recorded yet." />
        <BarList title="Traffic sources (UTM)" items={data.traffic_sources} empty="No campaign/UTM traffic recorded yet. Add ?utm_source=facebook to your links to track it." />
      </div>
    </AdminShell>
  );
}
